import { FC, memo, useCallback, useRef, useState } from 'react';

import { TabOption, Tabs } from '@/components/chariot-ui';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { ClusterByDropdown } from '@/components/nodeGraph/ClusterByDropdown';
import { useGraphStateContext } from '@/components/nodeGraph/core';

import { ActiveClustersSection } from './ActiveClustersSection';
import { ClusteringSection } from './ClusteringSection';
import { FiltersSection } from './FiltersSection';
import { MaxNodesInput } from './MaxNodesInput';
import { NodesVisibilitySection } from './NodesVisibilitySection';
import { ClusterWeightControlsProps } from './types';

const TabOptions: TabOption[] = [
  { label: 'Clusters', value: 'cluster' },
  { label: 'Nodes', value: 'settings' },
];

/**
 * ClusterWeightControls - Enhanced Graph Clustering and Visibility Control Panel
 *
 * This component provides the primary user interface for controlling how entities are
 * grouped and displayed in the NodeGraph visualization. It offers entity-specific
 * clustering options and visibility settings with manual entity type selection.
 *
 * Features:
 * - Manual entity type selection for clustering via dropdown
 * - Dynamic weight options that change based on selected entity type
 * - Visibility settings for attributes
 * - Asset/Risk/Technology controls
 * - Cluster selection with entity-specific logic
 * - Bulk operations for attribute visibility
 *
 * Entity-Specific Clustering:
 * - Assets: AS Name, Country, Status, etc.
 * - Risks: Severity, Status
 * - Technologies: Vendor, Product, Version
 *
 * UI Layout:
 * - Fixed positioning in bottom-left corner
 * - Tabbed interface (Clusters | Nodes)
 * - Entity type selection dropdown
 * - Scrollable content areas
 * - Responsive design with theme support
 *
 * Integration:
 * - Uses GraphStateContext for all UI state
 * - Uses GraphDataContext for filters and data
 * - Triggers cluster recalculation when weights change
 * - Updates graph visibility in real-time
 * - Provides entity selection and export functionality
 *
 * **BREAKING CHANGE**: This component now uses React Context instead of props.
 * All props have been removed. Use GraphStateProvider and GraphDataProvider.
 */
const ClusterWeightControlsComponent: FC<ClusterWeightControlsProps> = ({
  attributes,
  clusters,
  filters,
  setFilters,
}) => {
  // Get all state from contexts
  const { selection, entity, visibility, clustering, config } =
    useGraphStateContext();

  // Handle cluster click by updating selection state
  const handleClusterClick = useCallback(
    (value: string, nodes: string[]) => {
      selection.setCluster(value, new Set(nodes));
    },
    [selection]
  );
  const [activeTab, setActiveTab] = useState<TabOption>(TabOptions[0]);
  // Accordion state - only one section can be expanded at a time
  const [expandedSection, setExpandedSection] = useState<
    'filters' | 'clustering' | 'clusters' | null
  >('clustering'); // Default to clustering section open
  const panelRef = useRef<HTMLDivElement>(null);

  // Derived states from expandedSection
  const isFiltersExpanded = expandedSection === 'filters';
  const isClusteringExpanded = expandedSection === 'clustering';
  const isActiveClustersExpanded = expandedSection === 'clusters';

  // Accordion handler - expands clicked section and collapses others
  const handleSectionToggle = useCallback(
    (section: 'filters' | 'clustering' | 'clusters') => {
      setExpandedSection(prevSection =>
        prevSection === section ? null : section
      );
    },
    []
  );

  const activeWeight = clustering.weights.find(w => w.weight === 20);
  const activeAttribute = activeWeight?.name;

  // Memoize the tab change handler
  const handleTabChange = useCallback((tab: TabOption) => {
    setActiveTab(tab);
  }, []);

  return (
    <div
      ref={panelRef}
      className="border-chariot-stroke bg-chariot-background-primary fixed top-[58%] z-10 flex h-full max-h-[65vh] min-h-[400px] w-96 -translate-y-1/2 flex-col rounded-md border shadow-lg"
    >
      <div className="flex h-full flex-col p-4">
        {/* Select Entity Type Dropdown - Above tabs */}
        <div className="mb-3">
          <ClusterByDropdown
            clusterByEntity={entity.type}
            setClusterByEntity={entity.setType}
            onClearSelection={selection.clear}
          />
        </div>

        {/* Tabs - Fixed at top */}
        <Tabs
          tabs={TabOptions}
          selected={activeTab}
          onSelect={handleTabChange}
          variant="tertiary"
        />

        {/* Search Bar - Fixed below tabs, above scrollable content */}
        {activeTab.value === 'cluster' && filters && setFilters && (
          <div className="mt-4">
            <SearchBar
              size="sm"
              className="w-full"
              placeholder="Search"
              value={filters.search || ''}
              onChange={value => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setFilters((prev: any) => ({
                  ...prev,
                  search: value,
                }));
              }}
            />
          </div>
        )}

        {/* Tab Content - Single scroll area per tab */}
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
          {activeTab.value === 'cluster' && (
            <div className="flex flex-col gap-4 pr-2">
              {/* Set Filters Section */}
              {filters && setFilters && (
                <FiltersSection
                  filters={filters}
                  setFilters={setFilters}
                  clusterByEntity={entity.type}
                  isExpanded={isFiltersExpanded}
                  onToggle={() => handleSectionToggle('filters')}
                  panelRef={panelRef}
                />
              )}

              {/* Set Cluster By Relationship Section */}
              <ClusteringSection
                currentWeights={clustering.weights}
                onWeightChange={clustering.setWeight}
                isExpanded={isClusteringExpanded}
                onToggle={() => handleSectionToggle('clustering')}
              />

              {/* View Active Clusters Section - Show when clustering is active */}
              <ActiveClustersSection
                clusters={clusters}
                selectedClusterValue={selection.cluster}
                onClusterClick={handleClusterClick}
                activeAttribute={activeAttribute}
                isExpanded={isActiveClustersExpanded}
                onToggle={() => handleSectionToggle('clusters')}
              />
            </div>
          )}

          {activeTab.value === 'settings' && (
            <NodesVisibilitySection
              attributes={attributes}
              visibilityState={visibility.state}
              onVisibilityStateChange={visibility.setState}
            />
          )}
        </div>

        {/* Max Node Representation input - Fixed at bottom */}
        <MaxNodesInput
          maxNodes={config.maxNodes}
          onMaxNodesChange={config.setMaxNodes}
        />
      </div>
    </div>
  );
};

/**
 * Memoized ClusterWeightControls to prevent unnecessary re-renders
 * Only re-renders when props actually change
 *
 * Note: Most state is now managed via context, so we only need to
 * compare the few remaining props (attributes, clusters, filters).
 */
export const ClusterWeightControls = memo(
  ClusterWeightControlsComponent,
  (prevProps, nextProps) => {
    // Compare remaining props for equality
    return (
      prevProps.clusters.length === nextProps.clusters.length &&
      prevProps.attributes.length === nextProps.attributes.length &&
      prevProps.filters === nextProps.filters &&
      prevProps.setFilters === nextProps.setFilters
    );
  }
);
